import { responsiblePlhIdOfClient } from "./client-plh-lookup.js";
import type { RequestContext } from "../context/request-context.js";
import type { MeReportRepository, NdtProcedureRepository } from "../../repositories/operational-repositories.js";
import {
  type MeReport,
  type MeReleaseValidation,
  type MeValidationResult,
  isMeClosed,
  isMeHighTemperature,
  meEffectiveThickness,
  METHOD_A_LABEL,
  normalizeMeReportPoints,
  normalizeMeThermalFields,
  recomputeMeThermalCorrection,
  validateMeItems,
  validateMeReportCompleteness
} from "../../domain/engineering/me-report-domain.js";
import {
  formatFactor,
  formatTemperatureC,
  materialFamilyLabel,
  ME_HIGH_TEMPERATURE_LEGEND
} from "../../domain/engineering/me-thermal-correction.js";
import { isSubOsCompatibleWithInspector, type SubOsLike } from "../../domain/engineering/me-inspector-subos.js";
import { canActAsPlh } from "../../domain/identity/plh-eligibility.js";
import { resolveEffectiveTechnique } from "../../domain/ndt/ndt-procedure.js";
import { extractCatalogCode } from "../../domain/ndt/ndt-technical-catalog.js";
import { formatDecimalBR, resolveFinalLaudo, resolveLaudo } from "../../domain/engineering/me-measurements.js";
import { domainId, type MeasurementReportId, type EquipmentId, type ClientId } from "../../shared/kernel/identifiers.js";
import type { DocumentNumberAllocator } from "./document-numbering-service.js";
import type { DocumentVisual, TemplatedDocument } from "../../domain/studio/visual-design.js";
import { AppError, ErrorCode } from "../../shared/errors/app-error.js";
import type { DocumentSnapshot } from "../../infrastructure/documents/document-engine.js";
import { RbacAuthorizationService } from "../../permissions/authorization.js";
import { PermissionCode } from "../../permissions/permissions.js";
import type { DocumentReviewHooks } from "./technical-review-service.js";
import type { DocumentHoldHooks } from "./document-hold-service.js";
import type { AuditRecorder } from "../../audit/audit-recorder.js";
import { AuditAction } from "../../audit/audit-log.js";

const randomUUID = () => globalThis.crypto.randomUUID();

/** Function shown for a signatory row when the linked Professional has no functionTitle registered. */
const DEFAULT_SIGNATORY_TITLES: Record<string, string> = {
  INSPETOR: "INSPETOR",
  PLH: "ENGENHEIRO PLH",
  FISCALIZACAO: "FISCAL"
};

export class MeReportService {
  /** Revisão PLH central: bloqueia edição enquanto o ME está com o PLH e condiciona a assinatura à aprovação. */
  private reviewHooks: DocumentReviewHooks | null = null;

  /** Gate de uso de instrumentos M21; ligado pela plataforma, não pela UI. */
  private ndtAssetValidator: ((context: RequestContext, report: MeReport) => Promise<string[]>) | null = null;

  attachNdtAssetValidator(validator: (context: RequestContext, report: MeReport) => Promise<string[]>): void {
    this.ndtAssetValidator = validator;
  }

  constructor(
    private readonly repo: MeReportRepository,
    private readonly identityRepo?: any
  ) {}

  attachReviewHooks(hooks: DocumentReviewHooks): void {
    this.reviewHooks = hooks;
  }

  /**
   * CADASTRO DE PROCEDIMENTOS (NdtProcedure) — de onde `saveDraft` lê Vsa, k
   * e a Temperatura de Referência do MÉTODO A. Sem ele nenhum Procedimento é
   * conhecido e o resultado fica INCOMPLETE — nunca um valor presumido.
   * Ligado nos dois roots reais.
   */
  private ndtProcedureRepo: NdtProcedureRepository | null = null;

  attachNdtProcedureRepo(repo: NdtProcedureRepository): void {
    this.ndtProcedureRepo = repo;
  }

  /** HOLD: enquanto o ME está EM HOLD nada é salvo/validado/assinado — o status do módulo não muda. */
  private holdHooks: DocumentHoldHooks | null = null;

  attachHoldHooks(hooks: DocumentHoldHooks): void {
    this.holdHooks = hooks;
  }

  /**
   * SUB-OS × INSPETOR — o gate de servidor.
   *
   * A tela só oferece as Sub-OS do Inspetor, mas oferecer é desenho: uma
   * chamada direta pode mandar qualquer par. Este resolvedor devolve as
   * Sub-OS da OS do relatório para que `saveDraft` confira a
   * responsabilidade contra `SubWorkOrder.professionalId` — a fonte canônica
   * da seção 26, nunca `ProfessionalSubOsAccess`.
   *
   * É opcional para não quebrar composições e testes que não o fornecem;
   * quando ausente, a conferência simplesmente não roda (a tela continua
   * filtrando), e é por isso que ele é ligado nos dois roots reais.
   */
  private subOsResolver: ((context: RequestContext, clientId: string, workOrderId: string) => Promise<readonly SubOsLike[]>) | null = null;

  attachSubOsResolver(resolver: (context: RequestContext, clientId: string, workOrderId: string) => Promise<readonly SubOsLike[]>): void {
    this.subOsResolver = resolver;
  }

  private async assertSubOsBelongsToInspector(context: RequestContext, report: MeReport): Promise<void> {
    if (!this.subOsResolver || !report.osSubWorkOrderId || !report.osWorkOrderId) return;

    let subWorkOrders: readonly SubOsLike[] = [];
    try {
      subWorkOrders = await this.subOsResolver(context, String(report.clientId), String(report.osWorkOrderId));
    } catch {
      // Não dá para conferir: não é motivo para recusar uma gravação legítima.
      return;
    }

    if (!isSubOsCompatibleWithInspector(subWorkOrders, report.inspectorProfessionalId, report.osSubWorkOrderId)) {
      throw new AppError(ErrorCode.Validation, "Sub-OS does not belong to the report inspector", {
        safeMessage: "A Sub-OS selecionada não está sob responsabilidade do Inspetor deste relatório. Escolha uma Sub-OS do Inspetor ou ajuste a responsabilidade no cadastro de Sub-OS."
      });
    }
  }

  /** Auditoria do MÉTODO A. Sem `audit` ligado, `saveDraft` apenas não grava eventos. */
  private auditRecorder: AuditRecorder | null = null;

  attachAuditRecorder(audit: AuditRecorder): void {
    this.auditRecorder = audit;
  }

  /** Eventos de auditoria só do que MUDOU nesta gravação — regravar igual não gera eventos. */
  private async auditMethodACorrection(context: RequestContext, before: MeReport, after: MeReport): Promise<void> {
    const audit = this.auditRecorder;
    if (!audit) return;
    const previous = new Map((before.points || []).map(p => [p.key, p.hotCorrection ?? null]));
    const changed = (after.points || []).filter(p => {
      const now = p.hotCorrection ?? null;
      const old = previous.get(p.key) ?? null;
      if (!now) return false;
      return !old || old.status !== now.status || old.hotMeasuredThicknessMm !== now.hotMeasuredThicknessMm || old.approximateThicknessMm !== now.approximateThicknessMm || old.measurementTemperatureC !== now.measurementTemperatureC;
    });
    if (changed.length === 0) return;
    const record = (action: AuditAction, metadata: Record<string, unknown>) => audit.record(context, {
      action, entityType: "MeReport", entityId: String(after.id), previousState: null, newState: null, metadata: metadata as never
    });
    const corrected = changed.filter(p => p.hotCorrection!.status === "CORRECTED");
    if (corrected.length > 0) {
      await record(AuditAction.MethodAThicknessApproximated, {
        reportNumber: after.reportNumber,
        method: METHOD_A_LABEL,
        points: corrected.map(p => ({ point: p.id, tme: p.hotCorrection!.measurementTemperatureC, emq: p.hotCorrection!.hotMeasuredThicknessMm, fcA: p.hotCorrection!.correctionFactor, ea: p.hotCorrection!.approximateThicknessMm, procedimento: p.hotCorrection!.procedureCode }))
      });
    }
    const incomplete = changed.filter(p => p.hotCorrection!.status === "INCOMPLETE");
    if (incomplete.length > 0) {
      await record(AuditAction.MethodAMissingData, { reportNumber: after.reportNumber, points: incomplete.map(p => p.id), message: incomplete[0]!.hotCorrection!.message });
    }
  }

  private async assertNotOnHold(id: string): Promise<void> {
    if (!this.holdHooks) return;
    const hold = await this.holdHooks.state("ME", id);
    if (hold.onHold) throw new AppError(ErrorCode.Conflict, "ME report on hold", { safeMessage: hold.reason });
  }

  async getReport(context: RequestContext, id: string): Promise<MeReport | null> {
    const auth = new RbacAuthorizationService();
    auth.require(context, PermissionCode.MeReportView);
    const report = await this.repo.findById(context, id);
    return report ? normalizeMeReportPoints(report) : null;
  }

  async listByClient(context: RequestContext, clientId?: string): Promise<readonly MeReport[]> {
    const auth = new RbacAuthorizationService();
    auth.require(context, PermissionCode.MeReportView);
    return this.repo.listByClient(context, clientId ?? context.activeClientId);
  }

  /** Looks up the ME report already bound to an Inspection, so the page opens it instead of creating a second one. */
  async getByInspection(context: RequestContext, inspectionId: string): Promise<MeReport | null> {
    const auth = new RbacAuthorizationService();
    auth.require(context, PermissionCode.MeReportView);
    const report = await this.repo.findByInspection(context, inspectionId);
    return report ? normalizeMeReportPoints(report) : null;
  }

  /**
   * Creates a blank draft with a stable id and reportNumber, generated once
   * (never regenerated on subsequent saves — mirrors the Minuta's
   * trackingNumber stability rule, docs/RBAC.md).
   *
   * The owning client comes from the documentary context selected on the ME
   * page itself (`seed.clientId`); the session's global active client is only
   * a convenience fallback, never a requirement.
   */
  async createDraft(
    context: RequestContext,
    seed?: {
      clientId?: string | null;
      clientDisplayName?: string;
      projectId?: string | null;
      unitAreaId?: string | null;
      projectContract?: string;
      unitArea?: string;
      equipmentId?: string | null;
      equipmentTag?: string;
      equipmentTypeLabel?: string;
      osSubOs?: string;
      inspectionId?: string | null;
      inspectorProfessionalId?: string | null;
      inspectorName?: string;
    }
  ): Promise<MeReport> {
    const auth = new RbacAuthorizationService();
    auth.require(context, PermissionCode.MeReportCreate);

    const clientId = (seed?.clientId || context.activeClientId) as ClientId;
    if (!clientId) {
      throw new AppError(ErrorCode.Validation, "No client selected for the ME report", {
        safeMessage: "Selecione o Cliente do relatório antes de criar o Relatório ME."
      });
    }

    // One ME per Inspection: the page checks this before offering "Criar
    // Relatório ME", and the service enforces it so no second path (or a
    // double click) can produce a duplicate.
    if (seed?.inspectionId) {
      const existing = await this.repo.findByInspection(context, seed.inspectionId);
      if (existing) {
        throw new AppError(ErrorCode.Conflict, "ME report already exists for this inspection", {
          safeMessage: `Já existe um Relatório ME para esta Inspeção (Nº ${existing.reportNumber}). Abra o relatório existente em vez de criar outro.`
        });
      }
    }

    const now = new Date().toISOString();
    const reportNumber = await this.generateReportNumber(context, clientId, { projectId: seed?.projectId ?? null, unitAreaId: seed?.unitAreaId ?? null });

    const report: MeReport = {
      id: domainId<MeasurementReportId>(randomUUID()),
      clientId,
      reportNumber,
      clientReportNumber: "",
      revision: "00",
      status: "DRAFT",

      // IDs reais do contexto — é por eles que o workflow do PLH resolve
      // Documento → Projeto → Cliente.
      projectId: seed?.projectId ?? null,
      unitAreaId: seed?.unitAreaId ?? null,
      projectContract: seed?.projectContract || "",
      unitArea: seed?.unitArea || "",
      equipmentId: (seed?.equipmentId as EquipmentId) || null,
      equipmentTag: seed?.equipmentTag || "",
      equipmentTypeLabel: seed?.equipmentTypeLabel || "",
      osWorkOrderId: null,
      osSubWorkOrderId: null,
      osNumber: "",
      subOsNumber: "",
      osSubOs: seed?.osSubOs || "",
      testDate: "",
      periodStart: "",
      periodEnd: "",
      inspectionId: (seed?.inspectionId as any) || null,
      inspectorProfessionalId: (seed?.inspectorProfessionalId as any) ?? context.professionalId ?? null,
      inspectorName: seed?.inspectorName || "",

      procedureId: null,
      procedureRevision: "",
      referenceNormId: null,
      referenceNorm: "",
      acceptanceCriteriaId: null,
      acceptanceCriteria: "",
      material: "",
      materialFamilyCode: null,
      materialFamilyLabel: "",
      surfaceCondition: "",
      temperature: "",
      measurementTemperatureC: null,
      calibrationTemperatureC: null,
      thermalCorrection: null,
      thermometerCert: "",
      couplantId: null,
      couplant: "",
      calibrationBlockAssetId: null,
      calibrationBlock: "",
      calibrationBlockMaterial: "",
      calibrationBlockRange: "",
      calibrationBlockCert: "",
      calibrationBlockCertDate: "",
      calibrationBlockCertValidity: "",
      calibrationBlockCertLaboratory: "",
      measurementTechnique: "",
      measurementTechniqueOther: "",
      correctionFactor: "",

      deviceAssetId: null,
      deviceManufacturer: "",
      deviceModel: "",
      deviceSerialNumber: "",
      deviceCalibrationCert: "",
      deviceCalibrationDate: "",
      deviceCalibrationValidity: "",
      deviceCalibrationLaboratory: "",
      transducerAssetId: null,
      transducerManufacturer: "",
      transducerModel: "",
      transducerSerialNumber: "",
      transducerIdentification: "",
      transducerType: "",
      transducerFrequency: "",
      transducerDimension: "",
      transducerDimensionFrequency: "",

      points: [],
      croquiImage: null,
      comments: "",
      signatories: [],
      issueDate: now,
      clientDisplayName: seed?.clientDisplayName || "",

      signedAt: null,
      signedByUserId: null,
      signedByProfessionalId: null,

      createdAt: now,
      updatedAt: now,
      createdBy: context.userId || "system"
    };

    await this.repo.save(context, report);
    return report;
  }

  /**
   * "Salvar Rascunho": upserts the full report, preserving id/reportNumber/
   * revision/createdAt regardless of what the caller sends (section 28 —
   * never requires a fully-complete report to save; section 32 — the
   * document number must remain stable).
   */
  async saveDraft(context: RequestContext, report: MeReport): Promise<MeReport> {
    const auth = new RbacAuthorizationService();
    auth.require(context, PermissionCode.MeReportEdit);

    const existing = await this.repo.findById(context, report.id);
    if (!existing) {
      throw new AppError(ErrorCode.NotFound, "ME report not found", {
        safeMessage: "Relatório ME não encontrado."
      });
    }
    if (isMeClosed(existing.status)) {
      const label = existing.status === "FINALIZED" ? "finalizado" : "assinado";
      throw new AppError(ErrorCode.Conflict, `Relatório ${label} não pode mais ser editado.`, {
        safeMessage: `Relatório ${label} não pode mais ser editado.`
      });
    }

    // Inspetor só pode salvar relatórios em que seja o inspetor responsável
    if (!context.permissions.has(PermissionCode.MeReportAssignInspector) && context.professionalId) {
      if (existing.inspectorProfessionalId && String(existing.inspectorProfessionalId) !== String(context.professionalId)) {
        throw new AppError(ErrorCode.Authorization, "Você só possui permissão para editar relatórios sob sua responsabilidade técnica.");
      }
      if (report.inspectorProfessionalId && String(report.inspectorProfessionalId) !== String(context.professionalId)) {
        throw new AppError(ErrorCode.Authorization, "Você só possui permissão para editar relatórios sob sua responsabilidade técnica.");
      }
    }

    await this.assertNotOnHold(report.id);
    if (this.reviewHooks) {
      const state = await this.reviewHooks.state("ME", report.id);
      if (state.locked) throw new AppError(ErrorCode.Conflict, "ME report locked by PLH review", { safeMessage: state.reason });
    }

    const updated: MeReport = {
      ...normalizeMeReportPoints({ ...report }),
      id: existing.id,
      clientId: existing.clientId,
      reportNumber: existing.reportNumber,
      revision: existing.revision,
      createdAt: existing.createdAt,
      createdBy: existing.createdBy,
      // RASCUNHO → EM ELABORAÇÃO no primeiro salvamento bem-sucedido, e nunca
      // de volta: a situação é do servidor, não do que a tela mandar no corpo.
      status: existing.status === "DRAFT" ? "IN_PROGRESS" : existing.status,
      issueDate: new Date().toISOString(),
      updatedAt: new Date().toISOString()
    };

    // A correção MÉTODO A é derivada AQUI, na gravação, e não na montagem do
    // documento: o que fica persistido (FC_A, Ea e o retrato do Procedimento
    // usado) já é o valor final, e a tela, o Preview, o PDF e a AVR apenas
    // leem. Recalcular na impressão faria um Procedimento alterado depois
    // mudar a reimpressão de um relatório já aprovado.
    const procedure = this.ndtProcedureRepo && updated.procedureId
      ? await this.ndtProcedureRepo.findById(String(updated.procedureId))
      : null;
    recomputeMeThermalCorrection(updated, {
      now: updated.updatedAt,
      userId: context.userId ? String(context.userId) : null,
      procedure
    });

    // O PLH do documento é o responsável nomeado pelo CLIENTE, resolvido aqui
    // e não escolhido na tela. Gravar junto faz a tela, o Preview, o PDF e o
    // snapshot mostrarem a mesma pessoa — e é a mesma que o gate de aprovação
    // aceitaria.
    await this.applyResponsiblePlhSignatory(updated, updated.signatories as any[]);
    await this.assertSubOsBelongsToInspector(context, updated);

    await this.repo.save(context, updated);
    await this.auditMethodACorrection(context, existing, updated);
    if (this.reviewHooks) await this.reviewHooks.correctionSaved(context, "ME", updated.id);
    return updated;
  }

  /**
   * APROVAÇÃO DO PLH FECHA O RELATÓRIO. Chamado pelo ciclo central de revisão
   * quando o PLH aprova — não existe um segundo "FINALIZAR" para o usuário.
   *
   * É idempotente: reprocessar uma aprovação já aplicada não erra nem
   * reescreve o documento.
   */
  /**
   * A aprovação do PLH fecha o ME e CONGELA o documento aprovado.
   *
   * `approvedSnapshot` é o DocumentModel que o PLH efetivamente analisou.
   * Guardá-lo em `frozenSnapshot` é o que torna o PDF final consolidado, e
   * não uma remontagem: a montagem normal resolve ao vivo a imagem de
   * assinatura de cada Professional (ver `buildDocumentSnapshot`), de modo
   * que trocar uma assinatura no cadastro depois da aprovação faria um PDF
   * regerado sair com assinatura diferente da aprovada. Com o congelamento,
   * Inspetor, PLH e signatários ficam como estavam no ato da aprovação.
   *
   * Idempotente: reprocessar não reescreve um documento já fechado.
   */
  async finalizeOnPlhApproval(context: RequestContext, id: string, approvedSnapshot?: DocumentSnapshot | null): Promise<MeReport | null> {
    const report = await this.repo.findById(context, id);
    if (!report) return null;
    if (report.status === "FINALIZED" || report.status === "SIGNED") return report;
    if (this.ndtAssetValidator) {
      const issues = await this.ndtAssetValidator(context, report);
      if (issues.length) throw new AppError(ErrorCode.Validation, "Instrumentos END não liberados", {
        safeMessage: issues.join(" ")
      });
    }

    // O ciclo de revisão guarda apenas o MODELO (`snapshot.data`) — é o que
    // o PLH analisa na prévia. Para virar documento gerável de novo ele
    // precisa voltar ao envelope `DocumentSnapshot`, que carrega id, tipo,
    // revisão e principalmente o CLIENTE: sem `clientId`, o motor de PDF
    // recusa a geração com "Não foi possível identificar o cliente do
    // documento". Guardar o modelo cru aqui era um defeito silencioso — a
    // prévia abria vazia e o PDF falhava, só depois de finalizar.
    const frozen = approvedSnapshot ? this.envelopeOf(report, approvedSnapshot) : null;

    const finalized: MeReport = {
      ...report,
      status: "FINALIZED",
      ...(frozen ? { frozenSnapshot: frozen as MeReport["frozenSnapshot"] } : {}),
      updatedAt: new Date().toISOString()
    };
    await this.repo.save(context, finalized);
    return finalized;
  }

  /** "Validar Dados" (section 29): a stateless completeness check — never mutates status. */
  async validate(context: RequestContext, id: string): Promise<MeValidationResult> {
    await this.assertNotOnHold(id);
    const auth = new RbacAuthorizationService();
    auth.require(context, PermissionCode.MeReportValidate);

    const report = await this.repo.findById(context, id);
    if (!report) {
      throw new AppError(ErrorCode.NotFound, "ME report not found", {
        safeMessage: "Relatório ME não encontrado."
      });
    }
    const result = validateMeReportCompleteness(report);
    if (this.ndtAssetValidator) {
      const issues = await this.ndtAssetValidator(context, report);
      return { ...result, valid: result.valid && issues.length === 0, errors: [...result.errors, ...issues] };
    }
    return result;
  }

  /**
   * Gate de LIBERAÇÃO PARA O PLH — pendências dos ITENS 1 a 4, já
   * identificadas por item e por campo. Vive no serviço (e não só na tela)
   * porque a liberação precisa ser negada também quando chamada por RPC com
   * o documento incompleto: `disabled` no botão não é controle de acesso.
   *
   * Não existe aqui nenhum outro impeditivo: correção pendente, AVR, RI,
   * Book, Minuta ou END não bloqueiam a liberação do ME.
   */
  async validateForPlhRelease(context: RequestContext, id: string): Promise<MeReleaseValidation> {
    const auth = new RbacAuthorizationService();
    auth.require(context, PermissionCode.MeReportValidate);

    const report = await this.repo.findById(context, id);
    if (!report) {
      throw new AppError(ErrorCode.NotFound, "ME report not found", {
        safeMessage: "Relatório ME não encontrado."
      });
    }
    return validateMeItems(report);
  }

  /**
   * Envelope do documento em torno de um modelo já pronto. Aceita tanto o
   * modelo cru quanto um envelope completo (para tolerar registros gravados
   * antes desta correção), e sempre garante o `clientId` do relatório.
   */
  private envelopeOf(report: MeReport, approved: unknown): DocumentSnapshot {
    const candidate = approved as Partial<DocumentSnapshot> & { data?: unknown };
    const data = candidate && typeof candidate === "object" && "data" in candidate ? candidate.data : approved;
    return {
      id: report.reportNumber,
      type: "ME",
      revision: report.revision,
      documentNumber: report.reportNumber,
      clientId: report.clientId,
      createdAt: new Date(report.issueDate || report.updatedAt),
      data
    } as DocumentSnapshot;
  }

  /**
   * STUDIO INSPECT IA: o visual PUBLICADO entra no modelo documental quando
   * ele é montado. Aprovado/congelado = modelo congelado = visual da época.
   * Sem a ligação (testes, composições antigas), o modelo sai sem visual e o
   * builder imprime o padrão vigente — exatamente como antes.
   */
  private documentVisual: ((type: TemplatedDocument) => Promise<DocumentVisual>) | null = null;

  attachDocumentVisual(provider: (type: TemplatedDocument) => Promise<DocumentVisual>): void {
    this.documentVisual = provider;
  }

  /** Shared by "Visualizar Relatório" and "Gerar PDF" (section 30/31) — both consume the same DocumentModel/snapshot. */
  async buildDocumentSnapshot(context: RequestContext, id: string): Promise<DocumentSnapshot> {
    const auth = new RbacAuthorizationService();
    auth.require(context, PermissionCode.MeReportView);

    const report = await this.repo.findById(context, id);
    if (!report) {
      throw new AppError(ErrorCode.NotFound, "ME report not found", {
        safeMessage: "Relatório ME não encontrado."
      });
    }

    // Documento já aprovado/congelado: entrega-se o que foi aprovado, não uma
    // remontagem a partir dos cadastros de hoje.
    // Documento já aprovado/congelado: entrega-se o que foi aprovado. O
    // envelope é remontado a partir do relatório (id/cliente/revisão não
    // mudam depois de finalizado) e o MODELO vem congelado, intacto.
    if (report.frozenSnapshot) return this.envelopeOf(report, report.frozenSnapshot);

    normalizeMeReportPoints(report);
    // Relatório gravado antes destes campos: traz o texto legado para o
    // modelo atual apenas para LER. Nada é recalculado aqui — a ECT é a que
    // foi persistida na gravação (ver saveDraft).
    normalizeMeThermalFields(report);

    return {
      id: report.reportNumber,
      type: "ME",
      revision: report.revision,
      documentNumber: report.reportNumber,
      // The ME report carries its own client, so generating the document
      // never depends on a globally selected client.
      clientId: report.clientId,
      createdAt: new Date(report.issueDate),
      data: {
        visual: this.documentVisual ? await this.documentVisual("ME").catch(() => null) : null,
        client: report.clientDisplayName,
        contract: report.projectContract,
        site: report.unitArea,
        area: report.unitArea,
        tag: report.equipmentTag,
        equipment: report.equipmentTag,
        equipmentType: report.equipmentTypeLabel,
        osSubOs: report.osSubOs,
        osNumber: report.osNumber,
        subOsNumber: report.subOsNumber,
        revision: report.revision,
        date: report.testDate,
        periodStart: report.periodStart,
        periodEnd: report.periodEnd,
        issueDate: report.issueDate,
        clientReportNumber: report.clientReportNumber,
        inspector: report.inspectorName,

        procedure: report.procedureRevision,
        // Only the norm's code reaches the document — both preview and PDF
        // read this same value.
        norm: extractCatalogCode(report.referenceNorm),
        acceptanceCriteria: report.acceptanceCriteria,
        // FAMÍLIA DE MATERIAL: o rótulo congelado na escolha; o texto livre
        // antigo só aparece enquanto a família não tiver sido selecionada.
        material: report.materialFamilyLabel || materialFamilyLabel(report.materialFamilyCode) || report.material,
        materialFamilyCode: report.materialFamilyCode,
        surfaceCondition: report.surfaceCondition,
        temperature: report.measurementTemperatureC !== null && report.measurementTemperatureC !== undefined
          ? formatTemperatureC(report.measurementTemperatureC)
          : report.temperature,
        // Bloco de alta temperatura — o documento só o mostra quando existe.
        highTemperature: isMeHighTemperature(report),
        highTemperatureLegend: [...ME_HIGH_TEMPERATURE_LEGEND],
        measurementTemperatureC: report.measurementTemperatureC,
        thermalCorrection: report.thermalCorrection,
        materialSpecification: report.materialSpecification || "",
        // MÉTODO A — RESULTADO APROXIMADO: só existe em alta temperatura (somente leitura, nunca escolhido).
        correctionMethod: isMeHighTemperature(report) ? METHOD_A_LABEL : "",
        thermometerCert: report.thermometerCert,
        couplant: report.couplant,
        calibrationBlock: report.calibrationBlock,
        calibrationCert: report.calibrationBlockCert,
        calibrationCertValidity: report.calibrationBlockCertValidity,
        calibrationCertLaboratory: report.calibrationBlockCertLaboratory,
        technique: resolveEffectiveTechnique(report.measurementTechnique, report.measurementTechniqueOther),
        // O FC é POR PONTO, da calibração (coluna FC da tabela). O campo digitado
        // antigo só aparece em relatório convencional que o tenha preenchido.
        correctionFactor: isMeHighTemperature(report) ? "" : report.correctionFactor,

        deviceMaker: report.deviceManufacturer,
        deviceModel: report.deviceModel,
        deviceCert: report.deviceCalibrationCert,
        deviceValidity: report.deviceCalibrationValidity,
        deviceSerial: report.deviceSerialNumber,
        transducerMaker: report.transducerManufacturer,
        transducerModel: report.transducerModel,
        transducerFreq: report.transducerDimensionFrequency,
        transducerType: report.transducerType,
        transducerFrequency: report.transducerFrequency,
        transducerDimension: report.transducerDimension,
        transducerSerial: report.transducerSerialNumber,
        transducerId: report.transducerIdentification,

        // Presentation-ready rows: ids exactly as the user typed them,
        // thicknesses in 0,00 and the dimensional laudo derived from the
        // numbers — the same values the screen shows.
        measurements: report.points.map(p => {
          // Em alta temperatura o laudo julga a Ea, que é a espessura
          // aproximada; a Emq é a leitura do aparelho e fica registrada ao
          // lado, para rastreabilidade.
          const effective = meEffectiveThickness(report, p);
          return {
            id: p.id,
            component: p.component,
            location: p.location,
            nominalThickness: formatDecimalBR(p.nominalThickness),
            minThickness: formatDecimalBR(p.minThickness),
            actualThickness: formatDecimalBR(effective),
            // Emq / TME / Ea já formatados: o PDF imprime, não recalcula.
            emq: formatDecimalBR(p.actualThickness),
            tme: report.measurementTemperatureC !== null && report.measurementTemperatureC !== undefined
              ? String(report.measurementTemperatureC)
              : "",
            ea: formatDecimalBR(p.correctedThickness ?? null),
            // FC_A com 4 casas — lido do retrato gravado.
            fcA: p.hotCorrection && typeof p.hotCorrection.correctionFactor === "number" ? formatFactor(p.hotCorrection.correctionFactor) : "",
            procedureCode: p.hotCorrection?.status === "CORRECTED" ? p.hotCorrection.procedureCode : "",
            hotStatus: p.hotCorrection?.status ?? "",
            // The document prints the final decision: the override when the
            // user formally chose one, otherwise the calculated result.
            laudo: resolveFinalLaudo(p.minThickness, effective, p.laudoOverride as any),
            calculatedLaudo: resolveLaudo(p.minThickness, effective)
          };
        }),
        croqui: report.croquiImage,
        observations: report.comments,
        signatories: await this.resolveSignatories(report)
      }
    };
  }

  /**
   * PLH sign gate (mirrors engineering-service.ts#approveAndSignMinuta):
   * requires MeReportApprove + MeReportSign AND a real, qualified
   * Professional (canActAsPlh) — being DEVELOPER/ADMIN never satisfies this
   * on its own (section 23 of the ME command).
   */
  async signAsPlh(context: RequestContext, id: string): Promise<MeReport> {
    await this.assertNotOnHold(id);
    const auth = new RbacAuthorizationService();
    auth.require(context, PermissionCode.MeReportApprove);
    auth.require(context, PermissionCode.MeReportSign);

    const report = await this.repo.findById(context, id);
    if (!report) {
      throw new AppError(ErrorCode.NotFound, "ME report not found", {
        safeMessage: "Relatório ME não encontrado."
      });
    }
    if (report.status === "SIGNED") {
      throw new AppError(ErrorCode.Conflict, "Relatório já assinado.", { safeMessage: "Relatório já assinado." });
    }
    // Com ciclo de revisão aberto, assinar só depois de "Aprovado pelo PLH" (revisar → aprovar → assinar).
    if (this.reviewHooks) {
      const state = await this.reviewHooks.state("ME", id);
      if (state.review && !state.approved) {
        throw new AppError(ErrorCode.Conflict, "ME review not approved", { safeMessage: "O relatório só pode ser assinado depois de aprovado pelo PLH na revisão." });
      }
    }

    const user = this.identityRepo && context.userId ? await this.identityRepo.getUser(context.userId) : null;
    const professional = this.identityRepo && user?.professionalId ? await this.identityRepo.getProfessional(user.professionalId) : null;
    // A habilitação de PLH é conferida contra o CLIENTE DO DOCUMENTO, não
    // contra o Cliente selecionado na sessão. É a mesma base usada para
    // aprovar (`TechnicalReviewService.requireAssignedPlh`, via
    // `review.summary.clientId`) e pelos módulos irmãos — AVR, LP e a base de
    // END já liam `report.clientId`. Só o ME olhava a sessão, e o efeito era
    // absurdo: o mesmo PLH aprovava o relatório e, em seguida, era recusado
    // ao assiná-lo, porque nenhum Cliente estava selecionado na tela (ou
    // estava selecionado outro). A regra não foi afrouxada — continua
    // exigindo `canActAsPlh`; passou a ser aplicada sobre o Cliente certo.
    // Habilitação pelo CLIENTE DO DOCUMENTO: quem o Cliente nomeou responsável.
    const eligibility = canActAsPlh(user, professional, await responsiblePlhIdOfClient(this.identityRepo as never, String(report.clientId)));
    if (!eligibility.allowed) {
      throw new AppError(ErrorCode.Authorization, eligibility.reason || "Usuário não habilitado como PLH.", {
        safeMessage: eligibility.reason || "Você não está habilitado a assinar como PLH."
      });
    }

    const updated: MeReport = {
      ...report,
      status: "SIGNED",
      signedAt: new Date().toISOString(),
      signedByUserId: context.userId || null,
      signedByProfessionalId: professional.id,
      updatedAt: new Date().toISOString()
    };

    await this.repo.save(context, updated);
    if (this.reviewHooks) await this.reviewHooks.signed(context, "ME", updated.id);
    return updated;
  }

  /**
   * Signatories as the document needs them: the registered signature image
   * is resolved here (same source the RI uses) so the builder prints the
   * real image instead of a placeholder line. A signatory without a
   * registered signature keeps its identification and a blank signing space.
   *
   * The FISCAL row always exists, even when nobody was assigned to it — the
   * document must carry the field formally.
   */
  /**
   * SIGNATÁRIOS RESOLVIDOS PELO CONTEXTO, NÃO ESCOLHIDOS À MÃO.
   *
   *   INSPETOR  o Inspetor Responsável do próprio relatório
   *   PLH       o PLH responsável do CLIENTE DO DOCUMENTO (seção 24/28)
   *   FISCAL    pessoa do Cliente, que o sistema não tem como resolver —
   *             continua sendo texto opcional, e não é uma "seleção"
   *
   * A escolha manual do PLH que existia na tela podia apontar um Engenheiro
   * qualquer: o PDF então anunciava como aprovador alguém que o gate de
   * aprovação recusaria. O documento passa a mostrar quem de fato pode
   * aprová-lo.
   */
  private async resolveSignatories(report: MeReport): Promise<any[]> {
    const rows = [...report.signatories];
    if (!rows.some(s => s.role === "FISCALIZACAO")) {
      rows.push({ role: "FISCALIZACAO", professionalId: null, name: "", functionTitle: "FISCAL", credential: "", signatureImageId: null });
    }

    await this.applyResponsiblePlhSignatory(report, rows);

    const order: Record<string, number> = { INSPETOR: 0, PLH: 1, FISCALIZACAO: 2 };
    rows.sort((a, b) => (order[a.role] ?? 9) - (order[b.role] ?? 9));

    return Promise.all(rows.map(async s => {
      let signatureImage: string | null = null;
      if (s.professionalId && this.identityRepo?.getProfessionalSignature) {
        try {
          signatureImage = await this.identityRepo.getProfessionalSignature(s.professionalId);
        } catch {
          signatureImage = null;
        }
      }
      return {
        name: s.name,
        role: s.functionTitle || DEFAULT_SIGNATORY_TITLES[s.role] || "",
        cred: s.credential,
        signatureImageId: s.signatureImageId,
        signatureImage
      };
    }));
  }

  /**
   * Substitui a linha do PLH pelo responsável que o Cliente do documento
   * nomeou. Um relatório já assinado NÃO é tocado: o signatário histórico é
   * quem assinou, e trocar o Cliente de PLH depois não pode reescrever isso.
   *
   * Cliente sem PLH definido deixa a linha vazia e nomeada como tal, em vez
   * de manter um nome antigo que ninguém mais confirma.
   */
  private async applyResponsiblePlhSignatory(report: MeReport, rows: any[]): Promise<void> {
    if (report.status === "SIGNED" || report.signedByProfessionalId) return;
    if (!this.identityRepo) return;

    let professional: any = null;
    try {
      const plhId = await responsiblePlhIdOfClient(this.identityRepo as never, String(report.clientId));
      professional = plhId ? await this.identityRepo.getProfessional(plhId) : null;
    } catch {
      professional = null;
    }

    const index = rows.findIndex(s => s.role === "PLH");
    const resolved = {
      role: "PLH" as const,
      professionalId: professional?.id ?? null,
      name: professional?.name ?? "",
      functionTitle: professional?.functionTitle || DEFAULT_SIGNATORY_TITLES["PLH"] || "ENGENHEIRO PLH",
      credential: professional ? (professional.crea || professional.cft || professional.snqc || professional.sncc || "") : "",
      signatureImageId: professional?.signatureImageId ?? null
    };

    if (index >= 0) rows[index] = resolved;
    else rows.push(resolved);
  }

  /**
   * CONFIGURAÇÕES → NUMERAÇÃO. Com o alocador ligado, o número vem do modelo
   * configurado e de uma sequência ATÔMICA; sem ele (testes, composições que
   * não o fornecem), vale o gerador histórico.
   */
  private numbering: DocumentNumberAllocator | null = null;

  attachNumberAllocator(allocator: DocumentNumberAllocator): void {
    this.numbering = allocator;
  }

  private async generateReportNumber(context: RequestContext, clientId: string, refs: { projectId?: string | null; unitAreaId?: string | null } = {}): Promise<string> {
    const all = await this.repo.listByClient(context, clientId);
    const year = new Date().getFullYear();
    const prefix = `ME-${year}-`;
    const count = all.filter(r => r.reportNumber.startsWith(prefix)).length;
    if (this.numbering) {
      return this.numbering.allocate({
        documentType: "ME",
        clientId, projectId: refs.projectId ?? null, unitAreaId: refs.unitAreaId ?? null,
        legacyCount: async () => count,
        isTaken: async (candidate, scope) => {
          const pool = scope.perClient || !this.repo.listAll ? await this.repo.listByClient(context, clientId) : await this.repo.listAll();
          return pool.some(r => r.reportNumber === candidate);
        }
      });
    }
    return `${prefix}${String(count + 1).padStart(4, "0")}`;
  }
}
